import type { PaymentsConfigStatus, PlatformSettings } from '../types/admin';

/* ============================================================
   AS-1 (TASK-6e4bd7b5): pure view-model for the admin settings
   hub — Safe Deal overrides (null = follow appsettings), payment
   environment status, validation. No React, no IO: node-tested.
   ============================================================ */

/** Editable override field state kept by the hub form. */
export interface SafeDealOverrideState {
  /** null = not overridden (follow appsettings), otherwise the override value. */
  enabled: boolean | null;
  feeMoment: string | null;
  dealTtlDays: number | null;
  commissionRate: number | null;
  /** Editing text for the rates map (JSON); null = not overridden. */
  acquiringRatesText: string | null;
  autoPayoutEnabled: boolean | null;
  autoPayoutDelayHours: number | null;
}

/** Initialize override state from the loaded settings row. */
export function overridesFromSettings(settings: PlatformSettings): SafeDealOverrideState {
  return {
    enabled: settings.safeDealEnabled ?? null,
    feeMoment: settings.safeDealFeeMoment ?? null,
    dealTtlDays: settings.safeDealDealTtlDays ?? null,
    commissionRate: settings.safeDealCommissionRate ?? null,
    acquiringRatesText: settings.safeDealAcquiringRatesJson ?? null,
    autoPayoutEnabled: settings.safeDealAutoPayoutEnabled ?? null,
    autoPayoutDelayHours: settings.safeDealAutoPayoutDelayHours ?? null,
  };
}

/** Serialize the override state back onto a settings payload (nulls clear overrides). */
export function applyOverridesToSettings(
  settings: PlatformSettings,
  state: SafeDealOverrideState,
): PlatformSettings {
  return {
    ...settings,
    safeDealEnabled: state.enabled,
    safeDealFeeMoment: state.feeMoment,
    safeDealDealTtlDays: state.dealTtlDays,
    safeDealCommissionRate: state.commissionRate,
    safeDealAcquiringRatesJson: state.acquiringRatesText,
    safeDealAutoPayoutEnabled: state.autoPayoutEnabled,
    safeDealAutoPayoutDelayHours: state.autoPayoutDelayHours,
  };
}

export const FEE_MOMENTS = [
  { value: 'deal_closed', label: 'deal_closed — комиссия при закрытии сделки (полный возврат до выплаты)' },
  { value: 'payment_succeeded', label: 'payment_succeeded — комиссия сразу после оплаты' },
] as const;

/** Compact label for an effective fee_moment value. */
export function feeMomentLabel(value: string): string {
  return FEE_MOMENTS.find((m) => m.value === value)?.label ?? value;
}

/**
 * Parse the acquiring-rates editor text. Accepts strict JSON
 * ({"default":0.035,"bank_card":0.028}) or line form
 * (`default = 3.5%` / `bank_card = 0.028`). Returns an error string
 * mirroring the backend §12.4 contract (default key mandatory, band 0.004–0.04).
 */
export function parseAcquiringRates(text: string): { ok: true; json: string } | { ok: false; error: string } {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, error: 'Укажите тарифы (обязателен ключ default)' };

  let map: Record<string, number> | null = null;
  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        map = parsed as Record<string, number>;
      } else {
        return { ok: false, error: 'Тарифы должны быть JSON-объектом, например {"default":0.035,"bank_card":0.028}' };
      }
    } catch {
      return { ok: false, error: 'Некорректный JSON — ожидается объект вида {"default":0.035,"bank_card":0.028}' };
    }
  } else {
    map = {};
    for (const rawLine of trimmed.split(/\n|;/)) {
      const line = rawLine.trim();
      if (!line) continue;
      const match = /^([A-Za-z0-9_]+)\s*=\s*([0-9]+(?:\.[0-9]+)?)\s*(%?)$/.exec(line);
      if (!match) return { ok: false, error: `Строка «${line}» не вида «метод = ставка» (например: bank_card = 2.8%)` };
      const rate = Number(match[2]) / (match[3] === '%' ? 100 : 1);
      map[match[1]] = rate;
    }
  }

  const entries = Object.entries(map);
  if (entries.length === 0) return { ok: false, error: 'Укажите хотя бы один тариф (обязателен ключ default)' };
  if (!('default' in map)) return { ok: false, error: 'Ключ default обязателен — фолбэк для неизвестных методов' };
  for (const [method, rate] of entries) {
    if (!method.trim()) return { ok: false, error: 'Пустой ключ метода оплаты' };
    if (!Number.isFinite(rate) || rate < 0.004 || rate > 0.04) {
      return { ok: false, error: `Ставка «${method}» вне диапазона 0.004–0.04 (0.4%–4%)` };
    }
  }
  const normalized: Record<string, number> = {};
  for (const [method, rate] of entries) normalized[method] = Number(rate.toFixed(4));
  return { ok: true, json: JSON.stringify(normalized) };
}

/** Display form of a stored rates JSON (line form, percents). */
export function formatAcquiringRates(json: string | null): string {
  if (!json) return '';
  try {
    const parsed = JSON.parse(json) as Record<string, number>;
    return Object.entries(parsed)
      .map(([method, rate]) => `${method} = ${String(parseFloat((rate * 100).toFixed(2)))}%`)
      .join('\n');
  } catch {
    return json;
  }
}

/** Validate the full override state before save; returns user-facing errors. */
export function validateOverrides(state: SafeDealOverrideState): string[] {
  const errors: string[] = [];
  if (state.feeMoment !== null && !FEE_MOMENTS.some((m) => m.value === state.feeMoment)) {
    errors.push('fee_moment должен быть deal_closed или payment_succeeded');
  }
  if (state.dealTtlDays !== null && (!Number.isInteger(state.dealTtlDays) || state.dealTtlDays < 1 || state.dealTtlDays > 180)) {
    errors.push('Срок сделки: целое число дней 1–180');
  }
  if (state.commissionRate !== null && (state.commissionRate <= 0 || state.commissionRate > 0.5)) {
    errors.push('Комиссия платформы: число от 0 до 0.5 (например 0.10 = 10%)');
  }
  if (state.autoPayoutDelayHours !== null && (!Number.isInteger(state.autoPayoutDelayHours) || state.autoPayoutDelayHours < 0 || state.autoPayoutDelayHours > 720)) {
    errors.push('Задержка автоплатежей: целое число часов 0–720');
  }
  if (state.acquiringRatesText !== null) {
    const parsed = parseAcquiringRates(state.acquiringRatesText);
    if (!parsed.ok) errors.push(`Тарифы эквайринга: ${parsed.error}`);
  }
  return errors;
}

/** Whether any override is active (drives the «Переопределено» chips). */
export function hasAnyOverride(state: SafeDealOverrideState): boolean {
  return (
    state.enabled !== null ||
    state.feeMoment !== null ||
    state.dealTtlDays !== null ||
    state.commissionRate !== null ||
    state.acquiringRatesText !== null ||
    state.autoPayoutEnabled !== null ||
    state.autoPayoutDelayHours !== null
  );
}

/** Status line for the read-only environment card. */
export function environmentSummary(status: PaymentsConfigStatus): string[] {
  return [
    `Safe Deal: ${status.safeDealEffectiveEnabled ? 'включён' : 'выключен'} (конфиг: ${status.safeDealConfigEnabled ? 'вкл' : 'выкл'})`,
    `fee_moment: ${status.safeDealEffectiveFeeMoment} · комиссия ${(status.safeDealEffectiveCommissionRate * 100).toFixed(1).replace(/\.0$/, '')}% · TTL ${status.safeDealEffectiveDealTtlDays} дн.`,
    `Автовыплаты: ${status.safeDealEffectiveAutoPayoutEnabled ? 'включены' : 'выключены'} · холд ${status.safeDealEffectiveAutoPayoutDelayHours} ч`,
    `YooKassa: ${status.shopIdMasked ?? 'не настроен'} · секретный ключ ${status.secretKeyConfigured ? 'настроен' : 'отсутствует'}`,
    `Уведомления e-mail: ${status.emailEnabled ? `включены (${status.emailFromAddress} · ${status.emailHost})` : 'выключены (config Email)'}`,
  ];
}

/* ============================================================
   1c936a07: marketplace commission (per-provider) — pure helpers
   for the settings-hub fields. Percent units (15 = 15%), band
   (0; 50] mirroring the backend CommissionRates contract.
   ============================================================ */

export const COMMISSION_MAX_PERCENT = 50;

/**
 * Parse the global-default / per-provider percent input.
 * Blank = not overridden (null), otherwise a number in (0; 50]
 * with up to 2 decimals.
 */
export function parseCommissionPercent(
  text: string,
): { ok: true; value: number | null } | { ok: false; error: string } {
  const trimmed = text.trim().replace(',', '.');
  if (!trimmed) return { ok: true, value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return { ok: false, error: 'Комиссия: число процентов до 2 знаков после точки (например 12.5)' };
  }
  const value = Number(trimmed);
  if (value <= 0 || value > COMMISSION_MAX_PERCENT) {
    return { ok: false, error: `Комиссия: от 0 до ${COMMISSION_MAX_PERCENT} % (не включая 0)` };
  }
  return { ok: true, value };
}

/** Input text for a stored override (blank when not overridden). */
export function commissionPercentToText(percent: number | null | undefined): string {
  return percent == null ? '' : String(percent);
}

/** RU label for a provider row: override state vs the effective chain. */
export function commissionRowLabel(
  percent: number | null,
  globalPercent: number | null,
  configDefault: number,
): string {
  if (percent != null) return `переопределено: ${percent} %`;
  const effective = globalPercent ?? configDefault;
  return `по умолчанию: ${effective} %`;
}
